import { useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { http, formatApiError } from "../lib/api";
import { loadActivities } from "../lib/activities";
import { DIPLOMAS } from "../lib/diplomas";

const inp = "w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white";

export const NewActivityButton = ({ onCreated, testid = "new-activity-btn", className = "" }) => {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [diploma, setDiploma] = useState("");
  const [busy, setBusy] = useState(false);
  const close = () => { setOpen(false); setLabel(""); setDiploma(""); };
  const save = async () => {
    setBusy(true);
    try {
      const r = await http.post("/activities", { label, diploma: diploma || null });
      await loadActivities(true);
      toast.success(`Activité « ${r.data.label} » créée`);
      onCreated?.(r.data.code);
      close();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <button type="button" data-testid={testid} onClick={() => setOpen(true)} title="Créer une activité absente de la liste"
        className={`inline-flex items-center gap-1 text-[11px] font-semibold text-sky-700 hover:text-sky-900 hover:underline shrink-0 ${className}`}>
        <Plus className="h-3.5 w-3.5" /> Nouvelle activité
      </button>
      <Dialog open={open} onOpenChange={(v) => !v && close()}>
        <DialogContent className="max-w-sm" data-testid="new-activity-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Nouvelle activité</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <label className="block text-xs text-slate-500">Nom de l'activité *
              <input data-testid="new-activity-label" maxLength={40} autoFocus className={inp} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Ex. : Agent de sûreté aéroportuaire" /></label>
            <label className="block text-xs text-slate-500">Diplôme exigé pour l'affectation
              <select data-testid="new-activity-diploma" className={inp} value={diploma} onChange={(e) => setDiploma(e.target.value)}>
                <option value="">Aucun diplôme exigé</option>
                {DIPLOMAS.map((d) => <option key={d.code} value={d.code}>{d.label}</option>)}
              </select></label>
            <p className="text-[11px] text-slate-500">Disponible ensuite pour toute l'entreprise et sur tous les sites. Chaque site peut cocher ses activités.</p>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <button type="button" onClick={close} className="px-4 py-2 text-sm rounded-md border border-slate-300">Annuler</button>
            <button type="button" data-testid="new-activity-save" disabled={busy || label.trim().length < 2} onClick={save} className="px-4 py-2 text-sm rounded-md bg-sky-600 text-white font-semibold disabled:opacity-40">{busy ? "Création…" : "Créer"}</button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const SiteActivitiesPicker = ({ value, onChange, acts }) => {
  const set = new Set(value || []);
  const toggle = (c) => { const n = new Set(set); n.has(c) ? n.delete(c) : n.add(c); onChange([...n]); };
  return (
    <div className="border border-slate-200 rounded-md p-3 bg-slate-50/60" data-testid="site-activities">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Activités proposées sur ce site</div>
        <NewActivityButton testid="site-new-activity" onCreated={(c) => onChange([...set, c])} />
      </div>
      <div className="flex flex-wrap gap-1.5">
        {acts.map((a) => (
          <label key={a.code} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs cursor-pointer select-none transition-colors ${set.has(a.code) ? "border-sky-400 bg-sky-50 text-sky-900 font-semibold" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
            <input type="checkbox" data-testid={`site-activity-${a.code}`} className="h-3.5 w-3.5" checked={set.has(a.code)} onChange={() => toggle(a.code)} />{a.label}
          </label>
        ))}
      </div>
      <p className="text-[10px] text-slate-500 mt-2">Aucune case cochée : toutes les activités restent proposées dans le planning de ce site.</p>
    </div>
  );
};
